import { FormControl, FormLabel, Slider as SliderBase, SliderProps as SliderBaseProps, formLabelClasses, sliderClasses } from '@mui/material';
import React, { FC, ReactNode } from 'react';

import { Typography, TypographyProps } from '../Typography';


export type SliderProps = Omit<SliderBaseProps, 'value' | 'defaultValue'> & {
    label?: ReactNode;
    labelVariant?: TypographyProps['variant'];
    labelColor?: TypographyProps['color'];
    startValue?: number;
    value?: number;
    defaultValue?: number;
};
export const Slider: FC<SliderProps> = ({
    name, label, value = 0, startValue = 0, defaultValue = startValue,
    labelVariant = 'regularCaption1', labelColor = 'grayscale.shade90',
    sx, onChange, valueLabelFormat, min, max, ...props
}) => (
    <FormControl>
        {label && (
            <FormLabel
                sx={({ typography }) => ({
                    ...typography[labelVariant],
                    mb: 2,
                    color: labelColor,
                    [`&.${formLabelClasses.focused}`]: {
                        color: labelColor,
                    },
                })}
            >
                {label}
            </FormLabel>
        )}
        <SliderBase
            valueLabelDisplay='on'
            getAriaLabel={idx => `${name}_${idx}`}
            name={name}
            valueLabelFormat={(value, idx) => (
                <Typography variant='regularCaption1' color='grayscale.shade95'>
                    {valueLabelFormat
                        ? typeof valueLabelFormat === 'function'
                            ? valueLabelFormat(value, idx)
                            : valueLabelFormat
                        : value}
                </Typography>
            )}
            value={[
                value < defaultValue ? value : defaultValue,
                value > defaultValue ? value : defaultValue,
            ]}
            onChange={(e, value, activeThumb) =>
                onChange(e, value[0] !== defaultValue ? value[0] : value[1], activeThumb)}
            min={min}
            max={max}
            sx={[({ palette }) => ({
                '&': {
                    pt: 2,
                    mb: 6,
                },
                [`& .${sliderClasses.thumb}`]: {
                    ...value < defaultValue ? {
                        color: palette.infoCustom.red50,
                    } : {
                        display: 'none',
                    },
                    ...value === min && {
                        transform: 'translate(calc(-50% + 10px), -50%)',
                    },
                    [`&+.${sliderClasses.thumb}`]: {
                        ...value < defaultValue ? {
                            display: 'none',
                        } : {
                            display: 'flex',
                            color: palette.infoCustom.red50,
                        },
                        ...value === max && {
                            transform: 'translate(calc(-50% - 10px), -50%)',
                        },
                    },
                },
                [`& .${sliderClasses.track}`]: {
                    color: palette.infoCustom.red50,
                    ...value >= defaultValue && {
                        transform: 'translate(-2px, -50%)',
                    },
                },
                [`& .${sliderClasses.rail}`]: {
                    color: palette.grayscale.shade20,
                    opacity: 1,
                },
                [`& .${sliderClasses.valueLabel}`]: {
                    pt: 2,
                    px: 0,
                    backgroundColor: 'transparent',
                    '&::before': { display: 'none' },
                    [`&.${sliderClasses.valueLabelOpen}`]: {
                        transform: 'translateY(100%) scale(1)',
                    },
                },
            }),
            ...Array.isArray(sx) ? sx : [sx]]}
            {...props}
        />
    </FormControl>
);
